import { useQuery } from '@tanstack/react-query'

/**
 * Region outlines for the heat map, any country (WPS-232 #6): Natural Earth
 * admin-1 boundaries, one simplified GeoJSON file per country under
 * `public/geo/admin1/<CC>.json` (built by `scripts/geo/build_admin1.py`),
 * fetched only for the country on screen. Each feature's `properties.code` is
 * the region's ISO 3166-2 code - the same code the server counts by.
 */

type Position = number[]
type Ring = Position[]

export interface RegionFeature {
  type: 'Feature'
  properties: { code: string; name: string }
  geometry: { type: 'Polygon'; coordinates: Ring[] } | { type: 'MultiPolygon'; coordinates: Ring[][] }
}

export interface RegionCollection {
  type: 'FeatureCollection'
  features: RegionFeature[]
}

export function useRegionOutlines(country: string | null | undefined) {
  return useQuery<RegionCollection>({
    queryKey: ['geo-outlines', country ?? ''],
    queryFn: async () => {
      const res = await fetch(`${import.meta.env.BASE_URL}geo/admin1/${country}.json`)
      if (!res.ok) throw new Error(`No region outlines for ${country}`)
      return (await res.json()) as RegionCollection
    },
    enabled: !!country,
    staleTime: Infinity,
    retry: false,
  })
}

/** Every outer and inner ring of a region. */
export function ringsOf(feature: RegionFeature): Ring[] {
  return feature.geometry.type === 'Polygon'
    ? feature.geometry.coordinates
    : feature.geometry.coordinates.flat()
}

export interface ProjectedRegion {
  code: string
  name: string
  d: string
  /** Where the number sits: the centre of the region's largest ring. */
  centre: [number, number]
}

export interface ProjectedMap {
  width: number
  height: number
  regions: ProjectedRegion[]
  /** Boxes drawn around parts placed apart (e.g. Alaska, Hawaii). */
  insets: { x: number; y: number; w: number; h: number }[]
}


interface Box {
  x0: number
  y0: number
  x1: number
  y1: number
}

const boxOf = (points: Position[]): Box => ({
  x0: Math.min(...points.map((p) => p[0])),
  y0: Math.min(...points.map((p) => p[1])),
  x1: Math.max(...points.map((p) => p[0])),
  y1: Math.max(...points.map((p) => p[1])),
})
const union = (a: Box, b: Box): Box => ({
  x0: Math.min(a.x0, b.x0),
  y0: Math.min(a.y0, b.y0),
  x1: Math.max(a.x1, b.x1),
  y1: Math.max(a.y1, b.y1),
})
const area = (b: Box) => Math.max(1e-9, (b.x1 - b.x0) * (b.y1 - b.y0))

function ringArea(ring: Position[]): number {
  let a = 0
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) a += ring[j][0] * ring[i][1] - ring[i][0] * ring[j][1]
  return a / 2
}

function centroid(ring: Position[]): [number, number] {
  let cx = 0
  let cy = 0
  let a = 0
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const f = ring[j][0] * ring[i][1] - ring[i][0] * ring[j][1]
    cx += (ring[j][0] + ring[i][0]) * f
    cy += (ring[j][1] + ring[i][1]) * f
    a += f
  }
  return a ? [cx / (3 * a), cy / (3 * a)] : [ring[0][0], ring[0][1]]
}

/**
 * Project a country's regions to an SVG of `width` x `height` (equirectangular
 * at the country's middle latitude - true proportions there, and no polar
 * blow-up for a northern country the way Mercator gives Canada's Arctic; north
 * up). A region set apart from the rest - one whose absence would shrink the
 * map by more than 1.6x, or a small one far away (Alaska, Hawaii) - is drawn
 * in its own small inset at the bottom left, so the main landmass keeps its size.
 */
export function projectRegions(features: RegionFeature[], width = 975, height = 610): ProjectedMap {
  // Plain x/y (degrees east scaled to the middle latitude, degrees north),
  // with longitudes kept on the country's side of the antimeridian.
  const points = features.flatMap((f) => ringsOf(f).flat())
  const median = (values: number[]) => [...values].sort((a, b) => a - b)[Math.floor(values.length / 2)] ?? 0
  const midLon = median(points.map((p) => p[0]))
  const xScale = Math.cos((median(points.map((p) => p[1])) * Math.PI) / 180)
  const shapes = features.map((f) => {
    const rings = ringsOf(f).map((ring) =>
      ring.map(([lon, lat]) => {
        const x = lon - midLon > 180 ? lon - 360 : lon - midLon < -180 ? lon + 360 : lon
        return [x * xScale, -lat]
      }),
    )
    const main = rings.reduce((best, r) => (Math.abs(ringArea(r)) > Math.abs(ringArea(best)) ? r : best), rings[0] ?? [])
    return { f, rings, box: boxOf(rings.flat()), centre: centroid(main) }
  })
  if (!shapes.length) return { width, height, regions: [], insets: [] }

  // Set apart, one at a time, the region whose absence would shrink the area
  // the map has to fit the most - when it shrinks it by more than 1.6x
  // (Alaska, then Hawaii; overseas departments).
  type Shape = (typeof shapes)[number]
  const boxOfAll = (list: Shape[]) => list.map((s) => s.box).reduce(union)
  let rest = [...shapes]
  const apart = new Set<Shape>()
  while (rest.length > 3 && apart.size < 5) {
    const full = area(boxOfAll(rest))
    let worst: Shape | null = null
    let worstScore = 0
    for (const s of rest) {
      const others = boxOfAll(rest.filter((o) => o !== s))
      const ratio = full / area(others)
      // How far it sits from the rest, as a share of the rest's size (Hawaii).
      const gap = Math.max(0, others.x0 - s.box.x1, s.box.x0 - others.x1, others.y0 - s.box.y1, s.box.y0 - others.y1)
      const apartBy = gap / Math.max(others.x1 - others.x0, others.y1 - others.y0)
      const small = area(s.box) < area(others) / 4
      const score = ratio > 1.6 ? ratio : small && apartBy > 0.25 ? 1 + apartBy : 0
      if (score > worstScore) {
        worst = s
        worstScore = score
      }
    }
    if (!worst) break
    apart.add(worst)
    rest = rest.filter((s) => s !== worst)
  }
  const core = boxOfAll(rest)

  const insetW = width * 0.2
  const insetH = height * 0.2
  const insets: ProjectedMap['insets'] = []
  const frames = new Map<(typeof shapes)[number], (p: Position) => [number, number]>()
  const fit = (box: Box, x: number, y: number, w: number, h: number) => {
    const scale = Math.min(w / (box.x1 - box.x0 || 1), h / (box.y1 - box.y0 || 1))
    const ox = x + (w - (box.x1 - box.x0) * scale) / 2
    const oy = y + (h - (box.y1 - box.y0) * scale) / 2
    return (p: Position): [number, number] => [ox + (p[0] - box.x0) * scale, oy + (p[1] - box.y0) * scale]
  }
  const pad = 8
  const coreFrame = fit(core, pad, pad, width - 2 * pad, height - 2 * pad - (apart.size ? insetH : 0))
  ;[...apart].forEach((s, i) => {
    const x = pad + i * (insetW + pad)
    const y = height - insetH - pad
    insets.push({ x, y, w: insetW, h: insetH })
    frames.set(s, fit(s.box, x + 4, y + 4, insetW - 8, insetH - 8))
  })

  const regions = shapes.map((s) => {
    const project = frames.get(s) ?? coreFrame
    const d = s.rings
      .map((ring) => ring.map((p, i) => `${i ? 'L' : 'M'}${project(p)[0].toFixed(1)},${project(p)[1].toFixed(1)}`).join('') + 'Z')
      .join('')
    return { code: s.f.properties.code, name: s.f.properties.name, d, centre: project(s.centre) }
  })
  return { width, height, regions, insets }
}
